// =============================================================================
// VulnTracker — Auth Store (Zustand)
// =============================================================================
// SSO INTEGRATION:
//   The central auth at app.securusmind.com passes a JWT to this app via:
//   1. URL query param   → demo.vi.securusmind.com?token=<jwt>
//   2. Shared cookie     → __sm_token (domain=.securusmind.com)
//
//   Call initFromExternalToken() on app mount to pick it up.
//   If neither is present, falls back to bypass-token (demo mode).
// =============================================================================
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/types';
import api from '@/api/client';

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  setTokens: (access: string, refresh: string) => void;
  setUser: (user: User) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  fetchMe: () => Promise<void>;
  /** Read JWT from ?token= URL param or __sm_token cookie (SSO handoff). */
  initFromExternalToken: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: 'bypass-token',
      refreshToken: 'bypass-token',
      user: { id: '1f5b010f-82aa-4626-9eb5-dee43f97d46f', email: 'contact@securusmind.com', full_name: 'SecurusMind', role: 'admin' as const, is_active: true, created_at: '' } as User,
      isAuthenticated: true,
      isLoading: false,

      initFromExternalToken: () => {
        // 1. Check ?token= URL query param (redirect from central auth)
        const params = new URLSearchParams(window.location.search);
        const urlToken = params.get('token');
        if (urlToken) {
          // Strip token from URL without triggering a reload
          params.delete('token');
          const newSearch = params.toString();
          const cleanUrl =
            window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
          window.history.replaceState({}, '', cleanUrl);
          set({ accessToken: urlToken, isAuthenticated: true });
          return;
        }

        // 2. Check shared cookie set by central auth (domain=.securusmind.com)
        const cookieToken = document.cookie
          .split(';')
          .map((c) => c.trim().split('='))
          .find(([k]) => k === '__sm_token')?.[1];
        if (cookieToken) {
          set({ accessToken: cookieToken, isAuthenticated: true });
          return;
        }

        // 3. No external token — keep existing state (bypass-token for demo mode)
      },

      setTokens: (access: string, refresh: string) => {
        set({ accessToken: access, refreshToken: refresh, isAuthenticated: true });
      },

      setUser: (user: User) => {
        set({ user });
      },

      login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const response = await api.post('/auth/login', { email, password });
          const { access_token, refresh_token } = response.data;
          set({
            accessToken: access_token,
            refreshToken: refresh_token,
            isAuthenticated: true,
            isLoading: false,
          });
          await get().fetchMe();
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
        });
      },

      fetchMe: async () => {
        try {
          const response = await api.get('/auth/me');
          set({ user: response.data });
        } catch {
          // Keep existing user state on failure — don't wipe it out
        }
      },
    }),
    {
      name: 'vulntracker-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    },
  ),
);
